import React, { useEffect, useState, useCallback } from 'react';
import { useT } from '../../context/TenantContext';
import { useNavigate } from 'react-router-dom';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import SLABadge from '../../components/common/SLABadge';
import { StatusChip, PrioChip, LevelChip, STATUS_LABEL as STATUS, PRIO_LABEL as PRIO } from '../../components/common/Chip';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui';
import { useRealtime } from '../../hooks/useRealtime';
import NewTicketModal from './NewTicketModal';

export default function TicketList() {
  const { t } = useT();
  const navigate = useNavigate();
  const { agent } = useAuth();
  const [tickets,  setTickets]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [priority, setPriority] = useState('');
  const [level,    setLevel]    = useState('');
  const [toast,    setToast]    = useState(null);
  const [showNew,  setShowNew]  = useState(false);
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const PAGE_SIZE = 50;

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (search)   p.set('search',   search);
    if (status)   p.set('status',   status);
    if (priority) p.set('priority', priority);
    if (level)    p.set('level',    level);
    p.set('limit',  PAGE_SIZE);
    p.set('offset', (page - 1) * PAGE_SIZE);
    api.getTickets(`?${p}`)
      .then(res => { setTickets(res.data || []); setTotal(res.total ?? res.data?.length ?? 0); })
      .finally(() => setLoading(false));
  }, [search, status, priority, level, page]);

  useEffect(() => { load(); }, [load]);

  useRealtime(useCallback((event) => {
    if (['ticket_new','ticket_updated','ticket_escalated'].includes(event.type)) load();
  }, [load]));

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  async function takTicket(e, ticketId) {
    e.stopPropagation(); // no navegar
    if (!agent?.id) return;
    try {
      await api.updateTicket(ticketId, { assignee_id: Number(agent.id) });
      showToast('Ticket asignado ✓');
      load();
    } catch { showToast('Error al asignar'); }
  }

  const timeAgo = (d) => {
    const diff = (Date.now() - new Date(d)) / 1000;
    if (diff < 60) return 'ahora';
    if (diff < 3600) return `${Math.floor(diff/60)}m`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    return `${Math.floor(diff/86400)}d`;
  };

  const unassigned = tickets.filter(tk => !tk.assignee_id && !['resolved','forwarded','scheduled'].includes(tk.status)).length;

  return (
    <BackDeskLayout>

      {/* Toast */}
      {toast && (
        <div style={{position:'fixed',bottom:24,right:24,zIndex:9999,padding:'12px 20px',borderRadius:10,
          fontSize:13,fontWeight:600,background:'#F0FDF4',color:'#15803D',
          border:'1.5px solid #86EFAC',boxShadow:'0 4px 16px rgba(0,0,0,.12)'}}>
          ✓ {toast}
        </div>
      )}

      <div className="bd-table-wrap">
        <div className="bd-table-header">
          <span className="bd-table-title">{t('ticketlist.title')}</span>
          <Button variant="primary" onClick={() => setShowNew(true)} style={{flexShrink:0}}>
            {t('ticket.new_btn')}
          </Button>
          <input className="bd-search" placeholder={t('ticketlist.search')} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select className="bd-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">{t('ticketlist.filter_status')}</option>
            {Object.keys(STATUS).map(k => { const lbl = t('status.'+k) || STATUS[k]; return <option key={k} value={k}>{lbl}</option>; })}
          </select>
          <select className="bd-select" value={priority} onChange={e => { setPriority(e.target.value); setPage(1); }}>
            <option value="">{t('ticketlist.filter_prio')}</option>
            {Object.keys(PRIO).map(k => { const lbl = t('priority.'+k) || PRIO[k]; return <option key={k} value={k}>{lbl}</option>; })}
          </select>
          <select className="bd-select" value={level} onChange={e => { setLevel(e.target.value); setPage(1); }}>
            <option value="">{t('ticketlist.filter_level')}</option>
            {[1,2,3,4,5].map(l => <option key={l} value={l}>N{l}</option>)}
          </select>
        </div>
        {loading
          ? <div className="bd-loading"><div className="bd-spinner-sm"/>{t('general.loading')}</div>
          : tickets.length === 0
            ? <div className="bd-empty">{t('ticket.no_results')}</div>
            : <table>
                <thead>
                  <tr>
                    <th>{t('ticketlist.col_ref')}</th><th>{t('ticketlist.col_title')}</th><th>{t('ticketlist.col_status')}</th><th>{t('ticketlist.col_prio')}</th>
                    <th>{t('ticketlist.col_level')}</th><th>{t('ticketlist.col_agent')}</th><th>{t('ticketlist.col_sla')}</th><th>{t('ticketlist.col_updated')}</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(tk => {
                    const isMine = tk.assignee_id === agent?.id;
                    const isOpen = !['resolved','forwarded','scheduled'].includes(tk.status);
                    return (
                      <tr key={tk.id} onClick={() => navigate(`/backdesk/tickets/${tk.id}`)}
                        style={{cursor:'pointer'}}>
                        <td><span className="bd-ticket-ref">{tk.ref}</span></td>
                        <td>
                          <div className="bd-ticket-title">{tk.title}</div>
                          <div style={{fontSize:11,color:'var(--bd-muted)',marginTop:2}}>{tk.requester}</div>
                        </td>
                        <td><StatusChip status={tk.status} /></td>
                        <td><PrioChip priority={tk.priority} /></td>
                        <td><LevelChip level={tk.level} /></td>
                        <td style={{fontSize:12}}>
                          {isMine
                            ? <span style={{color:'#15803D',fontWeight:600}}>👤 Yo</span>
                            : tk.assignee_name
                              ? <span style={{color:'var(--bd-muted)'}}>{tk.assignee_name}</span>
                              : <span style={{color:'#9CA3AF',fontStyle:'italic'}}>{t('ticketlist.unassigned')}</span>
                          }
                        </td>
                        <td><SLABadge ticket={t} showTime={true}/></td>
                        <td style={{fontSize:12,color:'var(--bd-muted)'}}>{timeAgo(tk.updated_at)}</td>
                        <td onClick={e => e.stopPropagation()} style={{padding:'0 8px'}}>
                          {isOpen && !isMine && (
                            <button
                              onClick={(e) => takTicket(e, tk.id)}
                              title="Asignarme este ticket"
                              style={{
                                height:28,padding:'0 10px',borderRadius:6,fontSize:11,fontWeight:700,
                                cursor:'pointer',whiteSpace:'nowrap',
                                background:'#F0FDF4',color:'#15803D',border:'1.5px solid #86EFAC',
                              }}>
                              {t('ticketlist.take')}
                            </button>
                          )}
                          {isMine && isOpen && (
                            <span style={{fontSize:11,color:'#15803D',fontWeight:600}}>✓ Mío</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
        }
        {!loading && tickets.length > 0 && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderTop:'1px solid #F0F7FC'}}>
            <span style={{fontSize:12,color:'#9CA3AF'}}>
              {total} ticket{total===1?'':'s'} · página {page} de {totalPages}
            </span>
            <div style={{display:'flex',gap:8}}>
              <Button variant="secondary" size="sm" disabled={page<=1} onClick={() => setPage(p => p-1)}>‹ Anterior</Button>
              <Button variant="secondary" size="sm" disabled={page>=totalPages} onClick={() => setPage(p => p+1)}>Siguiente ›</Button>
            </div>
          </div>
        )}
      </div>
      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={(t) => { setShowNew(false); showToast('Ticket creado'); load(); if(t?.id) navigate(`/backdesk/tickets/${tk.id}`); }}/>}
    </BackDeskLayout>
  );
}
